import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useClaudeTask } from '@/hooks/useClaudeTasks';
import { TaskStatusBadge } from '@/components/claude-tasks/TaskStatusBadge';
import { TaskCommandBadge } from '@/components/claude-tasks/TaskCommandBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, X, RotateCcw, CheckCircle2, AlertCircle, MessageSquare, Wrench, ChevronDown, ChevronRight, Timer } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';

const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
};

interface LogEntry {
  type: 'tool' | 'result' | 'error' | 'comment' | 'tokens' | 'text';
  icon?: string;
  content: string;
  toolName?: string;
  command?: string;
  isSuccess?: boolean;
}

function parseStdout(stdout: string): LogEntry[] {
  const lines = stdout.split('\n');
  const entries: LogEntry[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Tool call
    if (line.startsWith('🔧 Tool:')) {
      const toolName = line.replace('🔧 Tool:', '').trim();
      let command = '';

      // Check for command on next lines
      i++;
      while (i < lines.length && !lines[i].startsWith('🔧') && !lines[i].startsWith('✓') && !lines[i].startsWith('❌') && !lines[i].startsWith('💬') && !lines[i].startsWith('📊')) {
        if (lines[i].trim()) {
          command += lines[i] + '\n';
        }
        i++;
      }

      entries.push({
        type: 'tool',
        icon: '🔧',
        content: toolName,
        toolName,
        command: command.trim(),
      });
      continue;
    }

    // Success result
    if (line.startsWith('✓ Result:')) {
      let content = line.replace('✓ Result:', '').trim();
      i++;
      while (i < lines.length && !lines[i].startsWith('🔧') && !lines[i].startsWith('✓') && !lines[i].startsWith('❌') && !lines[i].startsWith('💬') && !lines[i].startsWith('📊')) {
        if (lines[i].trim()) {
          content += '\n' + lines[i];
        }
        i++;
      }
      entries.push({
        type: 'result',
        icon: '✓',
        content: content.trim(),
        isSuccess: true,
      });
      continue;
    }

    // Error result
    if (line.startsWith('❌ Tool Error:')) {
      let content = line.replace('❌ Tool Error:', '').trim();
      i++;
      while (i < lines.length && !lines[i].startsWith('🔧') && !lines[i].startsWith('✓') && !lines[i].startsWith('❌') && !lines[i].startsWith('💬') && !lines[i].startsWith('📊')) {
        if (lines[i].trim()) {
          content += '\n' + lines[i];
        }
        i++;
      }
      entries.push({
        type: 'error',
        icon: '❌',
        content: content.trim(),
        isSuccess: false,
      });
      continue;
    }

    // Comment/thinking
    if (line.startsWith('💬')) {
      let content = line.replace('💬', '').trim();
      i++;
      while (i < lines.length && !lines[i].startsWith('🔧') && !lines[i].startsWith('✓') && !lines[i].startsWith('❌') && !lines[i].startsWith('💬') && !lines[i].startsWith('📊')) {
        if (lines[i].trim()) {
          content += '\n' + lines[i];
        }
        i++;
      }
      entries.push({
        type: 'comment',
        icon: '💬',
        content: content.trim(),
      });
      continue;
    }

    // Tokens
    if (line.startsWith('📊 Tokens:')) {
      entries.push({
        type: 'tokens',
        icon: '📊',
        content: line.replace('📊 Tokens:', '').trim(),
      });
      i++;
      continue;
    }

    // Regular text
    if (line.trim()) {
      entries.push({
        type: 'text',
        content: line,
      });
    }

    i++;
  }

  return entries;
}

function FormattedStdout({ stdout }: { stdout: string }) {
  const [expandedTools, setExpandedTools] = useState<Set<number>>(new Set());
  const entries = useMemo(() => parseStdout(stdout), [stdout]);

  const toggleTool = (index: number) => {
    const newExpanded = new Set(expandedTools);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTools(newExpanded);
  };

  const stats = useMemo(() => {
    const tools = entries.filter(e => e.type === 'tool').length;
    const errors = entries.filter(e => e.type === 'error').length;
    const successes = entries.filter(e => e.type === 'result' && e.isSuccess).length;
    return { tools, errors, successes };
  }, [entries]);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-1">
          <Wrench className="h-4 w-4 text-blue-600" />
          <span className="font-medium">{stats.tools}</span> tools used
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="font-medium">{stats.successes}</span> successful
        </div>
        {stats.errors > 0 && (
          <div className="flex items-center gap-1">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <span className="font-medium">{stats.errors}</span> errors
          </div>
        )}
      </div>

      {/* Log entries */}
      <div className="space-y-2">
        {entries.map((entry, index) => {
          if (entry.type === 'tool') {
            const isExpanded = expandedTools.has(index);
            return (
              <div key={index} className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleTool(index)}
                  className="w-full flex items-center gap-2 p-3 bg-blue-50 hover:bg-blue-100 text-left transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  )}
                  <Wrench className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="font-medium text-blue-900">{entry.content}</span>
                </button>
                {isExpanded && entry.command && (
                  <div className="p-3 bg-gray-50 border-t">
                    <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap">{entry.command}</pre>
                  </div>
                )}
              </div>
            );
          }

          if (entry.type === 'result') {
            return (
              <div key={index} className="flex gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-900 whitespace-pre-wrap flex-1">{entry.content}</div>
              </div>
            );
          }

          if (entry.type === 'error') {
            return (
              <div key={index} className="flex gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900 whitespace-pre-wrap flex-1">{entry.content}</div>
              </div>
            );
          }

          if (entry.type === 'comment') {
            return (
              <div key={index} className="flex gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <MessageSquare className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-900 whitespace-pre-wrap flex-1">{entry.content}</div>
              </div>
            );
          }

          if (entry.type === 'tokens') {
            return (
              <div key={index} className="flex gap-2 p-2 bg-gray-100 rounded text-xs text-gray-600">
                <span className="font-medium">📊 Tokens:</span> {entry.content}
              </div>
            );
          }

          if (entry.type === 'text') {
            return (
              <div key={index} className="text-sm text-gray-700 whitespace-pre-wrap px-3">{entry.content}</div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
}

export default function ClaudeTaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isCanceling, setIsCanceling] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);

  const { data: taskData, isLoading } = useClaudeTask(id || '', !!id);

  const task = taskData;

  const handleBack = () => {
    navigate('/tasks');
  };

  const handleCancel = async () => {
    if (!task) return;

    if (!confirm('Are you sure you want to cancel this task?')) {
      return;
    }

    setIsCanceling(true);
    try {
      const { error } = await supabase
        .from('claude_tasks')
        .update({ status: 'failed', result: { cancelled: true, cancelled_at: new Date().toISOString() } })
        .eq('id', task.id);

      if (error) {
        throw error;
      }

      toast({
        title: 'Task cancelled',
        description: 'The task has been cancelled successfully',
      });

      // Invalidate queries
      queryClient.invalidateQueries(['claudeTask', id]);
      queryClient.invalidateQueries(['claudeTasks']);
      queryClient.invalidateQueries(['claudeTasksStatistics']);
    } catch (error) {
      console.error('Error cancelling task:', error);
      toast({
        title: 'Error cancelling task',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsCanceling(false);
    }
  };

  const handleRestart = async () => {
    if (!task) return;

    if (!confirm('Are you sure you want to restart this task? It will be set to pending and queued for execution.')) {
      return;
    }

    setIsRestarting(true);
    try {
      const { error } = await supabase
        .from('claude_tasks')
        .update({
          status: 'pending',
          result: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', task.id);

      if (error) {
        throw error;
      }

      toast({
        title: 'Task restarted',
        description: 'The task has been reset to pending and will be executed soon',
      });

      // Invalidate queries
      queryClient.invalidateQueries(['claudeTask', id]);
      queryClient.invalidateQueries(['claudeTasks']);
      queryClient.invalidateQueries(['claudeTasksStatistics']);
    } catch (error) {
      console.error('Error restarting task:', error);
      toast({
        title: 'Error restarting task',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsRestarting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <div className="text-center py-8">Loading task...</div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="container mx-auto py-6 px-4">
        <div className="text-center py-8">Task not found</div>
      </div>
    );
  }

  const canCancel = task.status === 'pending' || task.status === 'running';
  const canRestart = true; // Always show restart button

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tasks
        </Button>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Task Details</h1>
          <div className="flex gap-2">
            {canRestart && (
              <Button
                variant="outline"
                onClick={handleRestart}
                disabled={isRestarting}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {isRestarting ? 'Restarting...' : 'Restart Task'}
              </Button>
            )}
            {canCancel && (
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={isCanceling}
              >
                <X className="h-4 w-4 mr-2" />
                {isCanceling ? 'Canceling...' : 'Cancel Task'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">ID</div>
                <div className="font-mono text-sm">{task.id}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Company Slug</div>
                <div className="font-medium">{task.slug}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Command</div>
                <TaskCommandBadge command={task.command} />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Status</div>
                <TaskStatusBadge status={task.status} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Created At</div>
                <div className="text-sm">
                  {format(new Date(task.created_at), 'MMM d, yyyy HH:mm:ss')}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Updated At</div>
                <div className="text-sm">
                  {format(new Date(task.updated_at), 'MMM d, yyyy HH:mm:ss')}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Claimed By</div>
                <div className="text-sm">{task.claimed_by || '—'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Claimed At</div>
                <div className="text-sm">
                  {task.claimed_at ? format(new Date(task.claimed_at), 'MMM d, yyyy HH:mm:ss') : '—'}
                </div>
              </div>
            </div>
            {task.claimed_at && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Duration</div>
                  <div className="text-sm flex items-center gap-1">
                    {(task.status === 'running' || task.status === 'pending') ? (
                      <>
                        <Timer className="h-3 w-3 text-blue-500 animate-pulse" />
                        <span className="text-blue-600 font-medium">
                          {formatDuration(Date.now() - new Date(task.claimed_at).getTime())}
                        </span>
                      </>
                    ) : (
                      <span>{formatDuration(new Date(task.updated_at).getTime() - new Date(task.claimed_at).getTime())}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Parameters */}
        <Card>
          <CardHeader>
            <CardTitle>Parameters</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
              <code className="text-sm">{JSON.stringify(task.params, null, 2)}</code>
            </pre>
          </CardContent>
        </Card>

        {/* Result */}
        {task.result && (
          <Card>
            <CardHeader>
              <CardTitle>Result</CardTitle>
            </CardHeader>
            <CardContent>
              {task.result.stdout ? (
                <div className="space-y-4">
                  <FormattedStdout stdout={task.result.stdout} />
                  {Object.keys(task.result).length > 1 && (
                    <details className="mt-4">
                      <summary className="text-sm font-medium text-gray-500 cursor-pointer hover:text-gray-700">
                        Show full result JSON
                      </summary>
                      <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto mt-2">
                        <code className="text-sm">{JSON.stringify(task.result, null, 2)}</code>
                      </pre>
                    </details>
                  )}
                </div>
              ) : (
                <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
                  <code className="text-sm">{JSON.stringify(task.result, null, 2)}</code>
                </pre>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
